import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
// kept so the shared .ai-card / .benefits-card rules stay in the bundle for
// the Inspection and Supply pages, which rely on them
import '../css/ai.css';
import '../css/uniface360.css';

const FEATURES = [
    {
        icon: 'fas fa-user-friends',
        title: 'Personnel Tracking',
        desc: 'Real-time monitoring of all personnel movements with AI identification and comprehensive tracking capabilities.',
        color: '#0c61c0'
    },
    {
        icon: 'fas fa-building',
        title: 'Space Monitoring',
        desc: 'Intelligent space utilization analytics with occupancy tracking and optimization insights.',
        color: '#1a8f5f'
    },
    {
        icon: 'fas fa-clock',
        title: 'Attendance System',
        desc: 'Automated attendance tracking with comprehensive reporting and time management features.',
        color: '#d97706'
    },
    {
        icon: 'fas fa-chart-line',
        title: 'AI-Powered Dashboard',
        desc: 'Advanced analytics and insights through machine learning for data-driven decision making.',
        color: '#d33f3f'
    },
    {
        icon: 'fas fa-shield-alt',
        title: 'HSE Team',
        desc: 'Health, Safety, and Environment personnel tracking and alerts for enhanced workplace safety.',
        color: '#6f42c1'
    },
    {
        icon: 'fas fa-lock',
        title: 'Security Team',
        desc: 'Dedicated monitoring for security staff and incident response with real-time alerts.',
        color: '#0d9488'
    },
    {
        icon: 'fas fa-life-ring',
        title: 'Rescue Team',
        desc: 'Rescue team presence and rapid deployment tracking for emergency situations.',
        color: '#b45309'
    },
    {
        icon: 'fas fa-exclamation-triangle',
        title: 'UN-Auth (Red Zone)',
        desc: 'Detection and alerting of unauthorized personnel in restricted areas with instant notifications.',
        color: '#d6336c'
    }
];

const STATS = [
    { icon: 'fas fa-percentage', value: '99%', label: 'Recognition Accuracy' },
    { icon: 'fas fa-building', value: '500+', label: 'Buildings Protected' },
    { icon: 'fas fa-shield-alt', value: '98%', label: 'Red Zone Accuracy' },
    { icon: 'fas fa-headset', value: '24/7', label: 'Monitoring & Support' }
];

const BENEFITS = [
    {
        icon: 'fas fa-shield-alt',
        title: 'Enhanced Security',
        desc: "Our AI-powered facial recognition system provides superior security by constantly monitoring who enters and exits your building. Uniface360 sends instant alerts for unauthorized access attempts and maintains a comprehensive log of all personnel movements.",
        features: [
            'Real-time notifications for security breaches',
            'Comprehensive audit logs for compliance',
            'Integration with existing security services'
        ],
        color: '#0c61c0'
    },
    {
        icon: 'fas fa-cogs',
        title: 'Operational Efficiency',
        desc: 'Automate attendance tracking and eliminate manual processes. Uniface360 streamlines operations by providing accurate attendance data, reducing administrative overhead, and enabling data-driven decision making.',
        features: [
            'Eliminate time fraud with AI verification',
            'Generate automated attendance reports',
            'Optimize resource allocation based on occupancy data'
        ],
        color: '#1a8f5f'
    },
    {
        icon: 'fas fa-video',
        title: 'Advanced Camera Technology',
        desc: 'Our system leverages cutting-edge camera technology with AI to provide comprehensive building coverage. View live feeds, replay recorded footage, and receive intelligent insights about space utilization and movement patterns.',
        features: [
            'Multi-floor camera integration',
            'Interactive building map with camera locations',
            'High-resolution footage with AI analytics'
        ],
        color: '#d97706'
    }
];

function AI() {
    const underlineRef = useRef(null);

    useEffect(() => {
        AOS.init({ duration: 800, once: true, offset: 60 });
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (underlineRef.current) {
                underlineRef.current.classList.add('animate');
            }
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className="uf-page">
            {/* ------------------------------------------------------- Hero --- */}
            <header className="uf-hero">
                <iframe
                    className="uf-hero__video"
                    src="https://player.vimeo.com/video/918429025?title=0&byline=0&muted=1&autoplay=1&autopause=0&controls=0&loop=1&app_id=122963"
                    title="Uniface360 overview"
                    frameBorder="0"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                />
                <div className="uf-hero__scrim" />

                <div className="container">
                    <div className="uf-hero__inner">
                        <span className="uf-eyebrow">
                            <i className="fas fa-microchip" />
                            Softwares &amp; AI
                        </span>
                        <h1 className="uf-hero__title">
                            Uniface<span>360</span>
                        </h1>
                        <h2 className="uf-hero__subtitle">AI-Powered Building Security</h2>
                        <div className="uf-rule" ref={underlineRef} />
                        <p className="uf-hero__desc">
                            Uniface360 delivers real-time personnel tracking, intelligent space
                            monitoring, and attendance management — all powered by AI. Upgrade your
                            facility&apos;s security, efficiency, and intelligence in one solution.
                        </p>
                        <div className="uf-hero__actions">
                            <Link to="/quote" className="uf-btn uf-btn--primary">
                                <i className="fas fa-play" /> Get Started Now
                            </Link>
                            <Link to="/contact" className="uf-btn uf-btn--ghost">
                                <i className="fas fa-desktop" /> Request a Demo
                            </Link>
                        </div>
                    </div>
                </div>

                <i className="fas fa-chevron-down uf-scroll-cue" aria-hidden="true" />
            </header>

            {/* --------------------------------------- Core capabilities --- */}
            <section className="uf-section">
                <div className="container">
                    <div className="uf-section-head" data-aos="fade-up">
                        <span className="uf-eyebrow">
                            <i className="fas fa-brain" /> Core Capabilities
                        </span>
                        <h2 className="uf-section-title">Everything your building needs, in one platform</h2>
                        <div className="uf-rule animate" />
                        <p className="uf-section-sub">
                            Uniface360 provides comprehensive AI-powered building security with advanced
                            monitoring and management capabilities.
                        </p>
                    </div>

                    <div className="row g-4">
                        {FEATURES.map((item, index) => (
                            <div
                                key={item.title}
                                className="col-lg-3 col-md-6"
                                data-aos="fade-up"
                                data-aos-delay={(index % 4) * 80}
                            >
                                <article className="uf-card" style={{ '--uf-accent': item.color }}>
                                    <span className="uf-card__icon">
                                        <i className={item.icon} />
                                    </span>
                                    <h3 className="uf-card__title">{item.title}</h3>
                                    <p className="uf-card__text">{item.desc}</p>
                                </article>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* -------------------------------------------------- Metrics --- */}
            <section className="uf-section pt-0">
                <div className="container">
                    <div className="uf-stats" data-aos="fade-up">
                        <h3 className="uf-section-title text-center mb-4">
                            Uniface360 Performance Metrics
                        </h3>
                        <div className="row g-4">
                            {STATS.map((stat) => (
                                <div className="col-6 col-md-3" key={stat.label}>
                                    <div className="uf-stat">
                                        <span className="uf-stat__icon">
                                            <i className={stat.icon} />
                                        </span>
                                        <p className="uf-stat__value">{stat.value}</p>
                                        <p className="uf-stat__label">{stat.label}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------- Benefits --- */}
            <section className="uf-section pt-0">
                <div className="container">
                    <div className="uf-section-head" data-aos="fade-up">
                        <span className="uf-eyebrow">
                            <i className="fas fa-star" /> Why Uniface360
                        </span>
                        <h2 className="uf-section-title">Built for security, efficiency, and insight</h2>
                        <div className="uf-rule animate" />
                    </div>

                    <div className="row g-4">
                        {BENEFITS.map((item, index) => (
                            <div
                                key={item.title}
                                className="col-lg-4 col-md-6"
                                data-aos="fade-up"
                                data-aos-delay={index * 120}
                            >
                                <article
                                    className="uf-card uf-card--benefit"
                                    style={{ '--uf-accent': item.color }}
                                >
                                    <span className="uf-card__icon">
                                        <i className={item.icon} />
                                    </span>
                                    <h3 className="uf-card__title">{item.title}</h3>
                                    <p className="uf-card__text">{item.desc}</p>
                                    <ul className="uf-list">
                                        {item.features.map((feature) => (
                                            <li key={feature}>
                                                <i className="fas fa-check-circle" />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ------------------------------------------------------ CTA --- */}
            <section className="uf-section pt-0">
                <div className="container">
                    <div className="uf-cta" data-aos="fade-up">
                        <span className="uf-cta__icon">
                            <i className="fas fa-rocket" />
                        </span>
                        <h2 className="uf-cta__title">Ready to Transform Your Building Security?</h2>
                        <p className="uf-cta__text">
                            Join the growing number of organizations that trust Uniface360 for their
                            security and attendance management needs.
                        </p>
                        <div className="uf-cta__actions">
                            <Link to="/quote" className="uf-btn uf-btn--light">
                                Get Started Now
                            </Link>
                            <Link to="/contact" className="uf-btn uf-btn--ghost">
                                Request a Demo
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default AI;
